import { defineStore } from 'pinia';
import type { AuthSubmitMode } from '@/entities/session/auth-errors';
import {
  persistAgentCode,
  persistReferralCode,
  storedAgentCode,
  storedReferralCode,
} from '@/shared/referral/invite-link';

interface AuthModalState {
  agentCode: string;
  mode: AuthSubmitMode;
  notice: string;
  open: boolean;
  /** 登录成功后回跳的站内路径；关闭弹层或消费后清空。 */
  pendingPath: string;
  referralCode: string;
}

/** 只接受站内相对路径，避免 open redirect。 */
export function safeAuthPendingPath(value: unknown): string {
  if (typeof value !== 'string') return '';
  const path = value.trim();
  if (!path.startsWith('/') || path.startsWith('//')) return '';
  if (path.includes('://') || path.includes('\\') || path.includes('\n') || path.includes('\r')) return '';
  if (path.length > 512) return '';
  return path;
}

/** 悬浮登录/注册弹层的全局开关：由路由守卫与头部入口统一驱动，覆盖在当前页之上。 */
export const useAuthModalStore = defineStore('authModal', {
  actions: {
    openAuth(payload?: {
      agentCode?: string;
      mode?: AuthSubmitMode;
      notice?: string;
      pendingPath?: string;
      referralCode?: string;
    }) {
      const referralCode = (payload?.referralCode ?? '').trim() || storedReferralCode();
      const agentCode = (payload?.agentCode ?? '').trim() || storedAgentCode();
      if (referralCode) persistReferralCode(referralCode);
      if (agentCode) persistAgentCode(agentCode);
      this.mode = payload?.mode ?? 'login';
      this.notice = (payload?.notice ?? '').trim();
      this.referralCode = referralCode;
      this.agentCode = agentCode;
      if (payload?.pendingPath !== undefined) {
        this.pendingPath = safeAuthPendingPath(payload.pendingPath);
      }
      this.open = true;
    },
    consumePendingPath() {
      const next = safeAuthPendingPath(this.pendingPath);
      this.pendingPath = '';
      return next;
    },
    closeAuth() {
      this.open = false;
      this.notice = '';
      this.referralCode = '';
      this.agentCode = '';
      this.pendingPath = '';
    },
  },
  state: (): AuthModalState => ({
    agentCode: '',
    mode: 'login',
    notice: '',
    open: false,
    pendingPath: '',
    referralCode: '',
  }),
});
